import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { Modal } from 'react-bootstrap-5';

import pluralize from 'pluralize';
import * as api from 'helpers/api';
import camelcaseKeys from 'camelcase-keys';

import DatabaseSwitch from '../transfers/database_switch';
import CopyRecordsConfirmation from '../transfers/copy_records_confirmation';
import { TRANSFER_ACTION_TYPE, TRANSFER_MODEL_TYPE } from '../../research_constants';
import Button from 'shared/button';

import { setTransferRecordBatch } from '../../store/actions/transfers';
import { hideCopyRecordsModal } from '../../store/actions/properties';
import { disableTransferRecords } from '../../store/actions/ui';

export function CopyRecordsModal({
  currentDatabaseId,
  currentUserDatabases,
  disableTransferRecords,
  shown,
  selectedPropertyIds,
  transferRecordsPath,
  onClose,
  setTransferRecordBatch
}) {
  const [database, setDatabase] = useState(null);
  const [showConfirmation, setShowConfirmation] = useState(false);
  const count = selectedPropertyIds.length;

  useEffect(() => {
    if (shown) {
      setShowConfirmation(false);
      setDatabase(null);
    }
  }, [shown]);

  const handleOnSelect = (database) => {
    setShowConfirmation(true);
    setDatabase(database);
  };

  const handleOnSubmit = () => {
    const data = {
      action: TRANSFER_ACTION_TYPE.COPY,
      selectedIds: selectedPropertyIds,
      sourceDatabaseId: currentDatabaseId,
      targetDatabaseId: database.id,
      modelType: TRANSFER_MODEL_TYPE
    };

    api.post(transferRecordsPath, { transferData: data }).then((response) => {
      const data = camelcaseKeys(response, { deep: true });
      setTransferRecordBatch(data.transferRecordBatch);
      disableTransferRecords();
      onClose();
    });
  };

  const currentDatabase = () => currentUserDatabases.find(db => db.id === currentDatabaseId);

  const renderBody = () => {
    if (showConfirmation && database) {
      return <CopyRecordsConfirmation sourceDatabase={currentDatabase()} targetDatabase={database} />;
    }

    return (
      <DatabaseSwitch
        availableDatabases={currentUserDatabases.filter(db => !db.backoffice)}
        currentDatabaseId={currentDatabaseId}
        selectDatabaseLabel="Copy to this Database"
        onSelectDatabase={handleOnSelect}
      />
    );
  };

  return (
    <Modal centered show={shown} size="lg" onHide={onClose}>
      <Modal.Header closeButton>
        <Modal.Title>Copy {count} {pluralize('Property', count)} to Another Database</Modal.Title>
      </Modal.Header>
      <Modal.Body className="overflow-y-auto" style={{ maxHeight: '60vh' }}>
        {renderBody()}
      </Modal.Body>
      {
        showConfirmation &&
        <Modal.Footer>
          <Button.Cancel onClick={onClose}>Cancel</Button.Cancel>
          <Button.Primary onClick={handleOnSubmit}>Confirm Copy</Button.Primary>
        </Modal.Footer>
      }
    </Modal>
  );
}

CopyRecordsModal.propTypes = {
  currentDatabaseId: PropTypes.number.isRequired,
  currentUserDatabases: PropTypes.array.isRequired,
  disableTransferRecords: PropTypes.func.isRequired,
  selectedPropertyIds: PropTypes.array.isRequired,
  setTransferRecordBatch: PropTypes.func.isRequired,
  shown: PropTypes.bool.isRequired,
  transferRecordsPath: PropTypes.string.isRequired,
  onClose: PropTypes.func.isRequired,
};

const mapStateToProps = state => ({
  currentUserDatabases: state.static.availableDatabases,
  currentDatabaseId: state.scope.databaseId,
  selectedPropertyIds: state.selectList.selectedPropertyIds,
  shown: state.properties.showCopyRecords,
  transferRecordsPath: state.paths.transferRecords
});

const mapDispatchToProps = {
  onClose: hideCopyRecordsModal,
  disableTransferRecords,
  setTransferRecordBatch
};

export default connect(mapStateToProps, mapDispatchToProps)(CopyRecordsModal);
